import {createIntl, createIntlCache} from "react-intl";
import {message} from "antd";
import queryString from "query-string";
import {createProject, updateProject, uploadProjectThumbnail, uploadProjectScene} from "api-v3";
import {createWorld, updateWorld, uploadWorldScene, uploadWorldThumbnail} from "api-v3";
import {store} from "store";
import Lang from "locale";
import {ACTION_TYPE, PROJECT_TYPE, EDITORMODE, WORLD_DEFAULT_ICON, STUDIO_LANG_TYPE, USER_GROUP} from "utils";
import * as TrackingUtil from "utils/TrackingUtil";
const {SEN_CODE, ICE_CODE, TECHLYMPIC, SWCHTU2023, SSAFY9_CODE, SSAFY10_CODE, SSAFY11_CODE, GANGWON_CODE, SEDIGIHISC_CODE} = USER_GROUP;
const HAS_GROUP_CODE = [SEN_CODE, ICE_CODE, TECHLYMPIC, SWCHTU2023, SSAFY9_CODE, SSAFY10_CODE, SSAFY11_CODE, GANGWON_CODE, SEDIGIHISC_CODE];
let url = new URLSearchParams(window.location.search);

// TODO: set as global config
message.config({
  maxCount: 1,
  duration: 2,
  className: "antd_message",
});

export const _saveProject = async context => {
  saveProject(context, saveProgress);
};

export const _autoSaveProject = async context => {
  saveProject(context, autoSaveLoading);
};

export const _publishWorld = async (context, publishInfo) => {
  publishWorld(context, publishInfo, saveProgress);
};

const createNewProject = async (id, lang) => {
  const {template, locale} = store.getState();
  const {templates} = template;
  const cache = createIntlCache();
  const intl = createIntl({locale, messages: Lang[locale]}, cache);

  const thumbnail = templates.find(template => template.id === id);
  const params = {
    name: intl.formatMessage(
      {id: "ID_STUDIO_CREATE_NEW_PROJECT_FORMAT"},
      {
        randomChar: String.fromCharCode(Math.floor(Math.random() * Math.floor(48)) + 48),
      },
    ),
    icon: thumbnail?.icon || WORLD_DEFAULT_ICON,
    language: lang === STUDIO_LANG_TYPE.TEXT ? PROJECT_TYPE.JS : PROJECT_TYPE.OOBC,
    id,
  };

  const search = queryString.parse(window.location.search);
  if (search.tag) {
    params.tag = search.tag;
  }

  try {
    await createProject(params);
    deleteTemplateURL();
  } catch (e) {
    message.error("Failed to save the project.");
  }
};

function deleteTemplateURL() {
  url.delete("template");
  url.delete("renderingMode"); // delete preset params
  url.delete("contentsType"); // delete preset params
  url.delete("token");
  window.history.pushState(null, null, `?${url}`);
}

const saveProject = async (context, progressCallback) => {
  // temporal code for multi select bug
  context.selectMultipleObject(undefined);
  const isTemplateExist = url.has("template");
  if (isTemplateExist) {
    await createNewProject(context.pId, context.devLang);
  }

  const imageFile = context.createIcon();
  const filename = context.pId + ".jpg";
  const response = await uploadProjectThumbnail({filename: filename});
  const thumbnailUrlData = response.data;

  // S3 THUMBNAIL UPLOAD
  await fetch(thumbnailUrlData.uploadUrl, {
    method: "PUT",
    body: imageFile,
  });

  const params = {
    icon: thumbnailUrlData.downloadUrl,
    language: context.devLang === STUDIO_LANG_TYPE.TEXT ? PROJECT_TYPE.JS : PROJECT_TYPE.OOBC,
  };

  try {
    progressCallback.start(context);
    await uploadScene(
      context,
      progress => {
        progressCallback.progress?.(context, progress);
      },
      "PROJECT",
    );
    await updateProject({id: context.pId}, params);
    progressCallback.success?.();
  } catch (e) {
    message.error("Failed to save the project.");
  } finally {
    progressCallback.end?.(context);
  }
};

const saveProgress = {
  start: context => displayProgress(context, "LOADING_START", 0, "SAVE_SCENE"),
  progress: (context, progress) => displayProgress(context, "LOADING_IN_PROGRESS", progress, "SAVE_SCENE"),
  end: context => displayProgress(context, "LOADING_END"),
};

const autoSaveLoading = {
  start: () => message.loading("Saving the project."),
  success: () =>
    message.success({
      content: "Project saved successfully.",
      style: {
        color: "#000000",
      },
    }),
};

const uploadScene = async (context, progressCallback, type) => {
  return new Promise(async (resolve, reject) => {
    const filename = context.pId + ".json";
    let sceneUrlData;

    if (type === "PROJECT") {
      const sceneResponse = await uploadProjectScene({filename: filename});
      sceneUrlData = sceneResponse.data;
    } else if (type === "WORLD") {
      const sceneResponse = await uploadWorldScene({filename: filename});
      sceneUrlData = sceneResponse.data;
    }

    const scenePublish = {
      default: context.sceneEdit,
      GUI: context.sceneGUI,
      version: context.versionManager.getVersion(),
    };
    const compressedScene = context.compressScene(scenePublish);
    const sceneFile = await convertToJson(compressedScene);

    const xhr = new XMLHttpRequest();
    xhr.open("PUT", sceneUrlData.uploadUrl, true);

    xhr.upload.addEventListener("progress", event => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        if (type === "PROJECT") {
          progressCallback(progress);
        } else if (type === "WORLD") {
          context.dispatchEvent(
            new CustomEvent(ACTION_TYPE.CHANGE_PROGRESS, {
              detail: {object: progress},
            }),
          );
        }
      }
    });

    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve(xhr.responseText);
      } else {
        reject(new Error("Scene upload failed"));
      }
    };

    xhr.onerror = () => {
      reject(new Error("Scene upload failed"));
    };

    xhr.send(sceneFile);
  });
};

const displayProgress = (context, eventName, progress, processName) => {
  context.dispatchEvent(
    new CustomEvent(eventName, {
      detail: {
        object: {
          progress: progress || null,
          processName: processName || null,
        },
      },
    }),
  );
};

const convertToJson = scene => {
  return new Promise(resolve => {
    let file = new Blob([JSON.stringify(scene)], {
      type: "text/json",
    });
    resolve(file);
  });
};

const publishWorld = async (context, publishInfo, progressCallback) => {
  let language = PROJECT_TYPE.OOBC;

  if (context.codeEditorMode === EDITORMODE.JAVASCRIPT_3D) {
    language = PROJECT_TYPE.JS;
  }

  // temporal code for multi select
  context.selectMultipleObject(undefined);

  if (publishInfo.thumbnail === null) {
    publishInfo.thumbnail = context.createIcon();
  }

  const thumbnailFilename = context.pId + ".jpg";
  const response = await uploadWorldThumbnail({filename: thumbnailFilename});
  const thumbnailUrlData = response.data;

  // S3 WORLD THUMBNAIL UPLOAD
  await fetch(thumbnailUrlData.uploadUrl, {
    method: "PUT",
    body: publishInfo.thumbnail,
  });

  const params = {
    name: publishInfo.name,
    icon: thumbnailUrlData.downloadUrl,
    language,
    description: publishInfo.description,
    isVisible: true,
    id: context.pId,
    competitionType: publishInfo.competitionType,
    phoneNumber: publishInfo.phoneNumber,
    agreementDate: publishInfo.agreementDate,
  };

  if (HAS_GROUP_CODE.includes(publishInfo.group)) {
    params.groupName = publishInfo.group;
    params.className = publishInfo.class;
  }

  try {
    // progressCallback.start(context);
    await uploadScene(
      context,
      progress => {
        // progressCallback.progress?.(context, progress);
      },
      "WORLD",
    );
    if (publishInfo.isWorldExist) {
      updateWorld({id: context.pId}, params);
    } else {
      createWorld(params).then(data => {
        if (data.id && context.userInfo.email) {
          TrackingUtil.sendAPEvent({
            event: "publish",
            "DLV - email": context.userInfo.email,
            "DLV - pid": context.pId,
            "DLV - type": language,
            "DLV - age": TrackingUtil.getAge(context.userInfo.birth),
            "DLV - gender": context.userInfo.gender,
            "DLV - live": TrackingUtil.isLiveUser(context.userInfo),
          });
        }
      });
    }
  } catch (error) {
    message.error("Failed to publish the world.");
  } finally {
    // progressCallback.end?.(context);
  }

  // ---------- Start to upload light version -----------
  // let lightJson = context.sceneEdit.toJSON();
  // lightJson.images.forEach((image, index) => {
  //   lightJson.images[index].url = "";
  // });
  // new THREE.ObjectLoader().parse(lightJson, scene => {
  //   scenePublish["default"] = scene;
  //   let lightFile = new Blob([JSON.stringify(scenePublish, null, 2)], {type: "text/json"});
  //   let lightUploader = new S3Uploader();
  //   lightUploader.upload(fileName + "_light", lightFile, "application/json", null, "published", lightProgress => {
  //     if (lightProgress === 100) {
  //       console.log("[Publish]: light version upload ok!");
  //     }
  //   });
  // });
  // --------- End of upload light version -----------
};
